<script>
import {useVuelidate} from '@vuelidate/core';
import {helpers, maxLength, required} from '@vuelidate/validators';
import $ from 'jquery';

/* COMPOSANTS */
import AkInputText from '@components/v2/input/AkInputText.vue';
import AkDropdown from '@components/v2/input/AkDropdown.vue';
import GaMessages from '@components/ga/layout/GaMessages.vue';
import AkCalendar from '@components/v2/input/AkCalendar.vue';
import AkFilesUpload from '@components/v2/input/AkFilesUpload.vue';
import AkSidebar from '@components/v2/general/AkSidebar.vue';
import AkButtonFooterSidebar from '@components/v2/general/AkButtonFooterSidebar.vue';
import AkInputPhone from '@components/v2/input/AkInputPhone.vue';
import AkFormSubmitted from '@components/general/AkFormSubmitted.vue';

/* MIXINS */
import messageMixin from '@mixins/messageMixin';
import companyMixin from '@mixins/companyMixin';
import permissionsMixin from '@mixins/permissionsMixin';
import formatMixin from '@mixins/formatMixin';
import validatorMixins from '@mixins/validatorMixins';
import civilitiesConst from '@mixins/const/civilitiesConst';

/* SERVICES */
import prestataireService from '@services/prestataireService';

export default {
  components: {
    AkButtonFooterSidebar,
    AkSidebar,
    AkFilesUpload,
    AkCalendar,
    GaMessages,
    AkDropdown,
    AkInputText,
    AkInputPhone,
    AkFormSubmitted,
  },
  mixins: [messageMixin, companyMixin, permissionsMixin, formatMixin, validatorMixins, civilitiesConst],
  setup() {
    return {v$: useVuelidate()};
  },
  data() {
    return {
      files: [],
      createSidebarVisible: false,
      prestataire: {},
      submitted: false,
    };
  },
  validations() {
    return {
      prestataire: {
        civility: {required},
        firstName: {
          required,
          maxLengthValue: maxLength(100),
          customPattern: helpers.withMessage(this.$t('bad_text_format'), this.yousignValidator),
        },
        lastName: {
          required,
          maxLengthValue: maxLength(100),
          customPattern: helpers.withMessage(this.$t('bad_text_format'), this.yousignValidator),
        },
        email: {
          required,
          maxLengthValue: maxLength(100),
          email: helpers.withMessage(
            "Le format de l'adresse e-mail est invalide. Veuillez vérifier et réessayer (ex. : nom@domaine.com)",
            this.emailValidator,
          ),
        },
        phone: {required, phoneValidator: this.phoneValidator},
        homePhone: {phoneValidator: this.phoneValidator},
        companyId: {required},
      },
    };
  },
  emits: ['creation'],
  mounted() {
    if (this.isGav()) this.prestataire.companyId = this.myCompanyId();
  },
  methods: {
    show() {
      this.createSidebarVisible = true;
      this.submitted = false;
      this.$nextTick(() => {
        $(this.$refs.content).focus();
      });
    },
    hide() {
      this.createSidebarVisible = false;
      this.prestataire = {};
    },
    create() {
      this.submitted = true;
      this.v$.$touch();
      if (this.v$.$error) {
        this.$refs.footerSidebar.init();
        return;
      }

      const prestataireDTO = {
        companyId: this.prestataire.companyId,
        contact: this.prestataire,
      };

      prestataireService
        .create(prestataireDTO, this.files)
        .then(() => {
          this.success(this.$t('prestataire.added'));
          this.prestataire = {};
          this.$emit('creation');
          this.hide();
        })
        .catch((e) => {
          const msg = this.msgForErr(this.$t, e);
          this.$refs.sidebarMessages.error(msg);
          this.$refs.footerSidebar.init();
        })
        .finally(() => {
          this.submitted = false;
        });
    },
  },
};
</script>

<template>
  <AkSidebar
    v-model:visible="createSidebarVisible"
    :baseZIndex="9998"
    :dismissable="true"
    @hide="hide"
    block-scroll
    position="right">
    <template #container>
      <div class="mb-4">
        <GaMessages ref="sidebarMessages" />
      </div>
      <div class="p-sidebar-header">
        <div class="p-sidebar-header-content">{{ $t('prestataire.create') }}</div>
        <button class="p-sidebar-close p-sidebar-icon p-link" type="button" @click="hide">
          <span
            class="p-sidebar-close-icon ga-icon ga-close_circle text-4xl text-grey-400 md:hover:text-grey-900 md:hover:bg-transparent" />
        </button>
      </div>
      <div ref="content" class="p-sidebar-content">
        <perfect-scrollbar class="h-full pr-7" :options="{suppressScrollX: true}">
          <AkFormSubmitted :submitted="submitted" class="flex flex-col gap-4 md:gap-2" reference="form">
            <span class="title-border secondary text-grey-900 text-xl font-bold mb-3">Informations</span>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-2">
              <AkDropdown
                v-model="prestataire.civility"
                :label="$t('prestataire.civility')"
                :options="civilitiesConst"
                :validator="v$.prestataire.civility" />
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-2">
              <AkInputText
                v-model="prestataire.lastName"
                :label="$t('prestataire.lastName')"
                :formatter="toUppercase"
                :validator="v$.prestataire.lastName" />
              <AkInputText
                v-model="prestataire.firstName"
                :label="$t('prestataire.firstName')"
                :formatter="toUppercase"
                :validator="v$.prestataire.firstName" />
            </div>
            <div class="grid grid-cols-1 gap-4 md:gap-2">
              <AkCalendar
                v-model="prestataire.birthDate"
                :label="$t('prestataire.birthDate')"
                :validator="v$.prestataire.birthDate"
                :date-format="'dd/mm/yy'"
                :maxDate="new Date()" />
            </div>
            <span class="title-border secondary text-grey-900 text-xl font-bold mb-3">Coordonnées</span>
            <div class="grid grid-cols-1 gap-4 md:gap-2">
              <AkInputText
                v-model="prestataire.email"
                :label="$t('prestataire.email')"
                :validator="v$.prestataire.email"
                type="email"
                icon="ga-icon ga-email" />
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-2">
              <AkInputPhone
                v-model="prestataire.phone"
                :label="$t('prestataire.phone')"
                :validator="v$.prestataire.phone"
                type="tel" />
              <AkInputPhone
                v-model="prestataire.homePhone"
                :label="$t('prestataire.homePhone')"
                :validator="v$.prestataire.homePhone"
                type="tel" />
            </div>
            <template v-if="isGas()">
              <span class="title-border secondary text-grey-900 text-xl font-bold mb-3"
                >Informations complémentaires</span
              >
              <AkDropdown
                v-model="prestataire.companyId"
                :label="$t('prestataire.company')"
                :options="companies"
                :validator="v$.prestataire.companyId"
                option-label="name"
                option-value="id" />
            </template>
            <span class="title-border secondary text-grey-900 text-xl font-bold mb-3">{{ $t('files') }}</span>
            <AkFilesUpload v-model="files" multiple />

            <AkButtonFooterSidebar
              ref="footerSidebar"
              :cancel-label="$t('cancel')"
              :validate-label="$t('add')"
              @action="create()"
              @cancel="hide()" />
          </AkFormSubmitted>
        </perfect-scrollbar>
      </div>
    </template>
  </AkSidebar>
</template>
